<template>
  <div class="nr1-dashboard-module animated fadeIn">
    <b-alert v-if="!effectiveCompanyId" variant="info" show>
      Selecione uma empresa para visualizar o Dashboard NR-1.
    </b-alert>

    <template v-else>
      <div class="dashboard-header d-flex justify-content-end mb-3">
        <display-mode-toggle
          v-model="displayMode"
          storage-key="dashboard:displayMode:nr1"
        />
      </div>
      <b-overlay :show="isLoading" rounded="sm" opacity="0.6" spinner-small>
        <b-card no-body class="chart-card mb-3">
          <b-card-body>
            <b-row>
              <b-col
                :md="hideQuestionnaireFilter ? 3 : 2"
                sm="6"
                class="mb-2 mb-md-0"
              >
                <label class="d-block filter-label">Modo de período</label>
                <b-form-select
                  class="filter-control"
                  v-model="filters.dateMode"
                  :options="dateModeOptions"
                  @change="loadDashboard"
                />
              </b-col>
              <b-col
                :md="hideQuestionnaireFilter ? 3 : 2"
                sm="6"
                class="mb-2 mb-md-0"
              >
                <label class="d-block filter-label">De</label>
                <b-form-input
                  class="filter-control"
                  v-model="filters.dateFrom"
                  type="date"
                  @change="loadDashboard"
                />
              </b-col>
              <b-col
                :md="hideQuestionnaireFilter ? 2 : 2"
                sm="6"
                class="mb-2 mb-md-0"
              >
                <label class="d-block filter-label">Até</label>
                <b-form-input
                  class="filter-control"
                  v-model="filters.dateTo"
                  type="date"
                  @change="loadDashboard"
                />
              </b-col>
              <b-col
                :md="hideQuestionnaireFilter ? 2 : 2"
                sm="6"
                class="mb-2 mb-md-0"
              >
                <label class="d-block filter-label">Área</label>
                <b-form-select
                  class="filter-control"
                  v-model="filters.area"
                  :options="areaOptions"
                  @change="onAreaChange"
                />
              </b-col>
              <b-col
                :md="hideQuestionnaireFilter ? 2 : 2"
                sm="6"
                class="mb-2 mb-md-0"
              >
                <label class="d-block filter-label">Unidade</label>
                <b-form-select
                  class="filter-control"
                  v-model="filters.businessUnit"
                  :options="businessUnitOptions"
                  @change="loadDashboard"
                />
              </b-col>
              <b-col
                v-if="!hideQuestionnaireFilter"
                md="2"
                sm="6"
                class="mb-2 mb-md-0"
              >
                <label class="d-block filter-label">Questionário</label>
                <b-form-select
                  class="filter-control"
                  v-model="filters.questionnaireId"
                  :options="questionnaireOptions"
                  @change="loadDashboard"
                />
              </b-col>
            </b-row>
          </b-card-body>
        </b-card>

        <b-card class="metrics-container mb-4">
          <div class="metrics-grid">
            <div
              v-for="metric in metricsCards"
              :key="metric.key"
              class="metric-section"
            >
              <div class="metric-header">
                <div class="metric-icon">
                  <i :class="metric.icon"></i>
                </div>
                <p class="metric-label">{{ metric.label }}</p>
              </div>
              <h3 class="metric-value">{{ metricDisplay(metric) }}</h3>
              <div class="progress-container mt-2">
                <div
                  class="progress-bar"
                  :style="{ width: `${metricProgress(metric)}%` }"
                ></div>
              </div>
            </div>
          </div>
        </b-card>

        <b-row>
          <b-col lg="12" class="mb-3">
            <b-card no-body class="dashboard-card chart-animation h-100">
              <b-card-body>
                <h5 class="card-title mb-3">
                  <i class="fas fa-layer-group mr-2"></i>
                  Risco por área
                </h5>
                <b-table
                  small
                  hover
                  responsive="sm"
                  :items="areaRisks"
                  :fields="areaFields"
                  empty-text="Sem dados para o filtro aplicado"
                  class="mb-0 nr1-table"
                >
                  <template #cell(overallRiskScore)="{ item }">
                    {{ item.overallRiskScore }}
                  </template>
                  <template #cell(overallRiskLevel)="{ item }">
                    <b-badge
                      :variant="riskLevelVariant(item.overallRiskLevel)"
                      >{{ item.overallRiskLevel }}</b-badge
                    >
                  </template>
                </b-table>
              </b-card-body>
            </b-card>
          </b-col>

          <b-col lg="6" class="mb-3">
            <b-card no-body class="dashboard-card chart-animation h-100">
              <b-card-body>
                <h5 class="card-title mb-3">
                  <i class="fas fa-exclamation-triangle mr-2"></i>
                  Top categorias críticas
                </h5>
                <b-table
                  small
                  hover
                  responsive="sm"
                  :items="topCategoryRisks"
                  :fields="categoryFields"
                  empty-text="Sem dados para o filtro aplicado"
                  class="mb-0 nr1-table"
                >
                  <template #cell(riskLevel)="{ item }">
                    <b-badge :variant="riskLevelVariant(item.riskLevel)">{{
                      item.riskLevel
                    }}</b-badge>
                  </template>
                </b-table>
              </b-card-body>
            </b-card>
          </b-col>

          <b-col lg="6" class="mb-3">
            <b-card no-body class="dashboard-card chart-animation h-100">
              <b-card-body>
                <h5 class="card-title mb-3">
                  <i class="fas fa-list-ol mr-2"></i>
                  Top perguntas críticas
                </h5>
                <b-table
                  small
                  hover
                  responsive="sm"
                  :items="topQuestionRisks"
                  :fields="questionFields"
                  empty-text="Sem dados para o filtro aplicado"
                  class="mb-0 nr1-table"
                >
                  <template #cell(riskLevel)="{ item }">
                    <b-badge :variant="riskLevelVariant(item.riskLevel)">{{
                      item.riskLevel
                    }}</b-badge>
                  </template>
                </b-table>
              </b-card-body>
            </b-card>
          </b-col>
        </b-row>

        <b-row>
          <b-col lg="6" class="mb-3">
            <b-card no-body class="dashboard-card chart-animation h-100">
              <b-card-body>
                <h5 class="card-title mb-3">
                  <i class="fas fa-chart-line mr-2"></i>
                  Evolução (média mensal)
                </h5>
                <b-table
                  small
                  hover
                  responsive="sm"
                  :items="evolution"
                  :fields="evolutionFields"
                  empty-text="Sem dados para o período selecionado"
                  class="mb-0 nr1-table"
                />
              </b-card-body>
            </b-card>
          </b-col>

          <b-col lg="6" class="mb-3">
            <b-card no-body class="dashboard-card chart-animation h-100">
              <b-card-body>
                <h5 class="card-title mb-3">
                  <i class="fas fa-clipboard-list mr-2"></i>
                  Questionários no recorte
                </h5>
                <b-table
                  small
                  hover
                  responsive="sm"
                  :items="questionnaireSummaries"
                  :fields="questionnaireFields"
                  empty-text="Sem questionários para o filtro aplicado"
                  class="mb-0 nr1-table"
                >
                  <template #cell(estimatedRiskLevel)="{ item }">
                    <b-badge
                      :variant="riskLevelVariant(item.estimatedRiskLevel)"
                      >{{ item.estimatedRiskLevel }}</b-badge
                    >
                  </template>
                  <template #cell(actions)="{ item }">
                    <b-button
                      size="sm"
                      variant="outline-primary"
                      class="action-btn"
                      @click="openQuestionnaireAnalytics(item.questionnaireId)"
                    >
                      Ver analytics
                    </b-button>
                  </template>
                </b-table>
              </b-card-body>
            </b-card>
          </b-col>
        </b-row>

        <b-row>
          <slot name="after-area-risk"></slot>
        </b-row>
      </b-overlay>
    </template>
  </div>
</template>

<script>
import Vue from "vue";
import { mapGetters } from "vuex";
import {
  getAreas,
  getBusinessUnits,
  getMacroDashboardAnalytics,
  getQuestionnaires,
} from "../../../api/nr1";
import DisplayModeToggle from "../DisplayModeToggle.vue";

const STORAGE_KEY = "dashboard:displayMode:nr1";

export default {
  name: "Nr1Dashboard",
  components: {
    DisplayModeToggle,
  },
  provide() {
    return {
      dashboardDisplayMode: this.dashboardDisplayMode,
    };
  },
  props: {
    company: {
      type: Object,
      default: null,
    },
    filterCompanyId: {
      type: [Number, String],
      default: null,
    },
    fixedQuestionnaireIds: {
      type: Array,
      default: () => [],
    },
    hideQuestionnaireFilter: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    const stored = localStorage.getItem(STORAGE_KEY);
    const initialMode = stored === "percent" ? "percent" : "absolute";
    return {
      displayMode: initialMode,
      dashboardDisplayMode: Vue.observable({ mode: initialMode }),
      filters: {
        dateMode: "submittedAt",
        dateFrom: "",
        dateTo: "",
        area: "",
        businessUnit: "",
        questionnaireId: "",
      },
      dateModeOptions: [
        { value: "submittedAt", text: "Data de resposta" },
        { value: "questionnaireWindow", text: "Vigência do questionário" },
      ],
      areas: [],
      businessUnits: [],
      questionnaires: [],
      isLoading: false,
      kpis: {
        criticalityIndex: 0,
        criticalityLevel: "Baixo",
        adoptionRate: 0,
        criticalAlerts: 0,
        overdueActionPlans: 0,
        totalResponses: 0,
        questionnaireCount: 0,
      },
      areaRisks: [],
      categoryRisks: [],
      questionRisks: [],
      evolution: [],
      questionnaireSummaries: [],
      areaFields: [
        { key: "area", label: "Área" },
        { key: "totalResponses", label: "Respostas" },
        { key: "overallRiskScore", label: "Score" },
        { key: "overallRiskLevel", label: "Nível" },
      ],
      categoryFields: [
        { key: "categoryName", label: "Categoria" },
        { key: "riskScore", label: "Score" },
        { key: "riskLevel", label: "Nível" },
      ],
      questionFields: [
        { key: "questionText", label: "Pergunta" },
        { key: "categoryName", label: "Categoria" },
        { key: "riskScore", label: "Score" },
        { key: "riskLevel", label: "Nível" },
      ],
      evolutionFields: [
        { key: "date", label: "Mês" },
        { key: "score", label: "Média" },
      ],
      questionnaireFields: [
        { key: "title", label: "Questionário" },
        { key: "avgAnswerValue", label: "Média respostas" },
        { key: "estimatedRiskScore", label: "Score estimado" },
        { key: "estimatedRiskLevel", label: "Nível" },
        { key: "actions", label: "Ações" },
      ],
    };
  },
  computed: {
    ...mapGetters(["profile"]),
    effectiveCompanyId() {
      if (this.filterCompanyId !== null && this.filterCompanyId !== undefined) {
        return parseInt(this.filterCompanyId);
      }

      if (this.company && this.company._id) {
        return parseInt(this.company._id);
      }

      if (this.profile && this.profile.company) {
        return parseInt(this.profile.company);
      }

      return null;
    },
    normalizedFixedQuestionnaireIds() {
      return Array.from(
        new Set(
          (this.fixedQuestionnaireIds || [])
            .map((item) => parseInt(item, 10))
            .filter((item) => !Number.isNaN(item))
        )
      );
    },
    availableAreas() {
      const questionnaireTargetAreas = Array.from(
        new Set(
          (this.questionnaires || []).flatMap((item) =>
            Array.isArray(item.targetAreas)
              ? item.targetAreas
                  .map((area) => String(area || "").trim())
                  .filter(Boolean)
              : []
          )
        )
      );

      if (!questionnaireTargetAreas.length) {
        return this.areas;
      }

      return this.areas.filter((item) =>
        questionnaireTargetAreas.includes(String(item || "").trim())
      );
    },
    areaOptions() {
      return [{ value: "", text: "Todas as áreas" }].concat(
        this.availableAreas.map((item) => ({ value: item, text: item }))
      );
    },
    businessUnitOptions() {
      return [{ value: "", text: "Todas as unidades" }].concat(
        this.businessUnits.map((item) => ({ value: item, text: item }))
      );
    },
    questionnaireOptions() {
      return [{ value: "", text: "Todos os questionários" }].concat(
        this.questionnaires.map((item) => ({
          value: String(item._id),
          text: item.title,
        }))
      );
    },
    topCategoryRisks() {
      return (this.categoryRisks || []).slice(0, 8);
    },
    topQuestionRisks() {
      return (this.questionRisks || []).slice(0, 8);
    },
    metricsCards() {
      return [
        {
          key: "criticality",
          label: "Índice de criticidade",
          icon: "fas fa-chart-line",
          value: this.kpis.criticalityIndex,
        },
        {
          key: "adoption",
          label: "Aderência",
          icon: "fas fa-check-circle",
          value: `${this.kpis.adoptionRate}%`,
        },
        {
          key: "criticalAlerts",
          label: "Alertas críticos",
          icon: "fas fa-bell",
          value: this.kpis.criticalAlerts,
        },
        {
          key: "overduePlans",
          label: "Planos atrasados",
          icon: "fas fa-clock",
          value: this.kpis.overdueActionPlans,
        },
        {
          key: "responses",
          label: "Respostas",
          icon: "fas fa-reply",
          value: this.kpis.totalResponses,
        },
        {
          key: "questionnaires",
          label: "Questionários",
          icon: "fas fa-file-alt",
          value: this.kpis.questionnaireCount,
        },
      ];
    },
  },
  watch: {
    effectiveCompanyId: {
      immediate: true,
      async handler() {
        if (!this.effectiveCompanyId) {
          return;
        }

        await this.loadFilterOptions();
        await this.loadDashboard();
      },
    },
    fixedQuestionnaireIds: {
      deep: true,
      async handler() {
        if (!this.effectiveCompanyId) {
          return;
        }

        await this.loadFilterOptions();
        await this.loadDashboard();
      },
    },
    displayMode(newMode) {
      this.dashboardDisplayMode.mode = newMode;
    },
  },
  methods: {
    metricDisplay(metric) {
      const valueStr = String(metric.value);
      if (this.displayMode !== "percent") return metric.value;
      // Already a percent value (e.g. "42%") — keep as is
      if (valueStr.includes("%")) return metric.value;
      // Score-like metrics keep absolute (não há denominador para %)
      if (metric.key === "criticality") return metric.value;
      const total = this.kpis.totalResponses;
      if (metric.key === "responses") return metric.value;
      if (metric.key === "questionnaires") return metric.value;
      if (!total || total <= 0) return "0,00%";
      const numeric = parseFloat(valueStr);
      if (Number.isNaN(numeric)) return metric.value;
      const pct = (numeric / total) * 100;
      return `${pct.toLocaleString("pt-BR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}%`;
    },
    riskLevelVariant(level) {
      const normalized = String(level || "").toLowerCase();
      if (normalized === "crítico" || normalized === "critico") {
        return "danger";
      }
      if (normalized === "alto") {
        return "warning";
      }
      if (normalized === "médio" || normalized === "medio") {
        return "info";
      }
      return "success";
    },
    buildRequestParams() {
      const params = {
        company: this.effectiveCompanyId,
        dateMode: this.filters.dateMode,
      };

      if (this.filters.dateFrom) {
        params.dateFrom = this.filters.dateFrom;
      }
      if (this.filters.dateTo) {
        params.dateTo = this.filters.dateTo;
      }
      if (this.filters.area) {
        params.area = this.filters.area;
      }
      if (this.filters.businessUnit) {
        params.businessUnit = this.filters.businessUnit;
      }
      if (this.normalizedFixedQuestionnaireIds.length) {
        params.questionnaireIds =
          this.normalizedFixedQuestionnaireIds.join(",");
      } else if (this.filters.questionnaireId) {
        params.questionnaireIds = this.filters.questionnaireId;
      }

      return params;
    },
    metricProgress(metric) {
      const numericValue = parseFloat(String(metric.value).replace("%", ""));
      if (Number.isNaN(numericValue) || numericValue <= 0) {
        return 0;
      }

      if (String(metric.value).includes("%")) {
        return Math.min(numericValue, 100);
      }

      const values = this.metricsCards
        .map((item) => parseFloat(String(item.value).replace("%", "")))
        .filter((value) => !Number.isNaN(value));
      const maxValue = Math.max(...values, 1);

      return Math.min((numericValue / maxValue) * 100, 100);
    },
    async loadFilterOptions() {
      const params = { company: this.effectiveCompanyId };
      const [areasRes, questionnairesRes] = await Promise.all([
        getAreas(params),
        getQuestionnaires({ ...params, active: true }),
      ]);

      this.areas = areasRes.data || [];
      const allQuestionnaires = questionnairesRes.data || [];
      this.questionnaires = this.normalizedFixedQuestionnaireIds.length
        ? allQuestionnaires.filter((item) =>
            this.normalizedFixedQuestionnaireIds.includes(
              parseInt(item._id, 10)
            )
          )
        : allQuestionnaires;

      const availableAreaValues = this.availableAreas.map((item) =>
        String(item || "").trim()
      );

      if (
        this.filters.area &&
        !availableAreaValues.includes(String(this.filters.area).trim())
      ) {
        this.filters.area = "";
      }

      await this.loadBusinessUnits();
    },
    async loadBusinessUnits() {
      const params = { company: this.effectiveCompanyId };
      if (this.filters.area) {
        params.area = this.filters.area;
      }

      const response = await getBusinessUnits(params);
      this.businessUnits = response.data || [];
    },
    async onAreaChange() {
      this.filters.businessUnit = "";
      await this.loadBusinessUnits();
      await this.loadDashboard();
    },
    async loadDashboard() {
      if (!this.effectiveCompanyId) {
        return;
      }

      this.isLoading = true;
      try {
        const params = this.buildRequestParams();
        this.$emit("filters-change", { ...params });

        const response = await getMacroDashboardAnalytics(params);
        const data = response.data || {};

        this.kpis = data.kpis || this.kpis;
        this.areaRisks = data.areaRisks || [];
        this.categoryRisks = data.categoryRisks || [];
        this.questionRisks = data.questionRisks || [];
        this.evolution = data.evolution || [];
        this.questionnaireSummaries = data.questionnaireSummaries || [];
      } finally {
        this.isLoading = false;
      }
    },
    openQuestionnaireAnalytics(questionnaireId) {
      this.$router.push(`/nr1/questionarios/${questionnaireId}/analytics`);
    },
  },
};
</script>

<style scoped>
.nr1-dashboard-module {
  padding-bottom: 0.5rem;
}

.metrics-container {
  border: none;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  background: white;
}

.metrics-grid {
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  align-items: stretch;
  min-height: 110px;
}

.metric-section {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 1.25rem 1rem;
  text-align: center;
}

.metric-header {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;
  margin: 0 auto 0.45rem auto;
}

.metric-icon {
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  color: #000000;
}

.metric-label {
  margin: 0;
  font-size: 0.82rem;
  color: #000000;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  line-height: 1.2;
  white-space: normal;
}

.metric-value {
  font-size: 2rem;
  font-weight: 600;
  margin: 0;
  color: #000000;
  line-height: 1;
}

.progress-container {
  width: 75%;
  height: 5px;
  background-color: #e2e8f0;
  border-radius: 2px;
  overflow: hidden;
  margin-left: auto;
  margin-right: auto;
}

.progress-bar {
  height: 100%;
  background: #4299e1;
  border-radius: 2px;
  transition: width 0.4s ease;
}

.metric-section:not(:last-child) {
  border-right: 1px dashed #cbd5e0;
}

.dashboard-card {
  border: none;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  background: white;
}

.card-title {
  color: #2d3748;
  font-weight: 600;
}

.nr1-table ::v-deep thead th {
  color: #718096;
  font-size: 0.8rem;
  font-weight: 700;
  text-transform: uppercase;
  border-top: none;
}

.nr1-table ::v-deep tbody td {
  vertical-align: middle;
}

.filter-label {
  font-size: 0.8rem;
  font-weight: 600;
  margin-bottom: 0.35rem;
}

.nr1-dashboard-module ::v-deep .chart-card .filter-control,
.nr1-dashboard-module ::v-deep .chart-card select.filter-control,
.nr1-dashboard-module ::v-deep .chart-card input.filter-control {
  border: 1px solid #94a3b861 !important;
  border-radius: 30px;
  background-color: #ffffff;
  outline: none;
}

.nr1-dashboard-module ::v-deep .chart-card .filter-control:focus,
.nr1-dashboard-module ::v-deep .chart-card select.filter-control:focus,
.nr1-dashboard-module ::v-deep .chart-card input.filter-control:focus {
  border-color: #3b82f6 !important;
  box-shadow: 0 0 0 0.12rem rgba(59, 130, 246, 0.2) !important;
}

.action-btn {
  white-space: nowrap;
}

@media (max-width: 991px) {
  .metrics-grid {
    grid-template-columns: 1fr;
    min-height: auto;
  }

  .metric-section:not(:last-child) {
    border-right: none;
    border-bottom: 1px dashed #cbd5e0;
  }

  .metric-section {
    padding: 1rem;
  }
}

@media (max-width: 1280px) and (min-width: 992px) {
  .metrics-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
    min-height: auto;
  }

  .metric-section {
    border-right: none;
    border-bottom: 1px dashed #cbd5e0;
  }

  .metric-section:nth-last-child(-n + 3) {
    border-bottom: none;
  }

  .metric-section:not(:nth-child(3n)) {
    border-right: 1px dashed #cbd5e0;
  }
}
</style>
